import axios from 'axios';
import { WizardData } from '../context/WizardContext';

interface ProgressData {
  sessionId: string;
  customerId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  currentStep: number;
  stepName: string;
  formData: WizardData;
  lastUpdated: string;
  isComplete: boolean;
  createdAt: string;
}

class MongoApiService {
  private apiUrl: string | null = null;

  constructor() {
    // You'll need to create an API endpoint that connects to MongoDB
    // For now, we'll use a placeholder URL
    this.apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';
  }

  async saveProgress(progressData: ProgressData): Promise<void> {
    if (!this.apiUrl) {
      throw new Error('API URL not configured');
    }

    try {
      await axios.post(`${this.apiUrl}/progress/save`, progressData);
    } catch (error) {
      console.error('Failed to save to MongoDB API:', error);
      throw error;
    }
  }

  async getIncompleteSessions(): Promise<ProgressData[]> {
    if (!this.apiUrl) {
      return [];
    }

    try {
      const response = await axios.get(`${this.apiUrl}/progress/incomplete`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch from MongoDB API:', error);
      return [];
    }
  }

  async getCompletedSessions(): Promise<ProgressData[]> {
    if (!this.apiUrl) {
      return [];
    }

    try {
      const response = await axios.get(`${this.apiUrl}/progress/completed`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch completed sessions from MongoDB API:', error);
      return [];
    }
  }

  async getProgress(sessionId: string): Promise<ProgressData | null> {
    if (!this.apiUrl) {
      return null;
    }

    try {
      const response = await axios.get(`${this.apiUrl}/progress/${sessionId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null; // Session not found
      }
      console.error('Failed to fetch progress from MongoDB API:', error);
      throw error;
    }
  }

  async deleteSession(sessionId: string): Promise<void> {
    if (!this.apiUrl) {
      throw new Error('API URL not configured');
    }

    try {
      await axios.delete(`${this.apiUrl}/progress/${sessionId}`);
    } catch (error) {
      console.error('Failed to delete from MongoDB API:', error);
      throw error;
    }
  }
}

export const mongoApiService = new MongoApiService();